import React, { createContext, useContext, useEffect, useState } from 'react';
import AACChromeInternalAPI, {
  InternalChromeConfig,
} from '@amzn/aac-chrome-internal-api';
import { getChromeData } from 'src/commons/apis';
import { InternalChromeApiMetricPublisher } from 'src/commons/utils/metrics';
import { logger } from 'src/commons/utils/logger';
import { isBtswContext, isFromBtswWc, isFromTrim } from 'src/commons/utils';
import { LoadingStatus } from 'src/commons/types';
import { updateInternalChromeConfig } from 'src/app/slice';
import { store } from 'src/app/store';
import { getTrimInternalChromeConfig } from 'src/commons/utils/helpers';

export { InternalChromeConfig, Feature } from '@amzn/aac-chrome-internal-api';

export interface InternalChromeConfigContextState {
  loadingStatus: LoadingStatus;
  config: InternalChromeConfig;
}

const InternalChromeConfigContext =
  createContext<InternalChromeConfigContextState>({
    loadingStatus: 'loading',
    config: {} as InternalChromeConfig,
  });

export const useInternalChromeConfigContext =
  (): InternalChromeConfigContextState => {
    const context = useContext(InternalChromeConfigContext);
    if (context === undefined) {
      throw new Error(
        'useInternalChromeConfigContext must be used within a InternalChromeConfigContextProvider',
      );
    }
    return context;
  };

const getInternalChromeContext = () => {
  try {
    return window.AACChrome.InternalAPI.getChromeContext();
  } catch (e) {
    return AACChromeInternalAPI.getChromeContext();
  }
};

export const InternalChromeConfigContextProvider: React.FC = ({ children }) => {
  const isFromTrimValue = isFromTrim();
  const isAjaxUsed = !isFromTrimValue && !isBtswContext() && !isFromBtswWc();

  const initialContext = isAjaxUsed
    ? ({} as InternalChromeConfig)
    : isFromTrimValue
      ? getTrimInternalChromeConfig()
      : getInternalChromeContext();

  const [loadingStatus, setLodingStatus] = useState<LoadingStatus>(
    isAjaxUsed ? 'loading' : 'success',
  );
  const [config, setConfig] = useState<InternalChromeConfig>(initialContext);

  const ajaxLoadInternalChromeConfigData = async () => {
    try {
      const { internalChromeConfig } = await getChromeData();
      setConfig(internalChromeConfig);
      setLodingStatus('success');
      InternalChromeApiMetricPublisher.publishCounterMonitor('initFailure', 0);
    } catch (e: any) {
      setLodingStatus('error');
      InternalChromeApiMetricPublisher.publishCounterMonitor('initFailure', 1);
      logger.error('failed to fetch chromeData:', e);
    }
  };

  useEffect(() => {
    if (loadingStatus === 'loading') {
      ajaxLoadInternalChromeConfigData();
    } else if (loadingStatus === 'success') {
      // cannot pass in the entire 'config' object since it is not serializable (contains getters, setters etc.)
      // only pass in required fields to the redux store
      store.dispatch(
        updateInternalChromeConfig({
          deviceType: config.deviceType,
          isMobileWebView: config.isMobileWebView,
          forceDesktopSite: config.forceDesktopSite,
          entityId: config.entityId,
          isInternalView: config.isInternalView,
        } as InternalChromeConfig),
      );
    }
  }, [loadingStatus]);

  return (
    <InternalChromeConfigContext.Provider
      value={{
        loadingStatus,
        config,
      }}
    >
      {children}
    </InternalChromeConfigContext.Provider>
  );
};
